import { Route, Routes } from 'react-router'
import { createGlobalStyle } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { NativeCapabilitiesContext } from '@blade/ui-kit'
import { TerminalAccessGate } from '@root/features/access'
import { AuthenticatedOnly } from '@root/features/auth/authenticate'
import {
  AuthRoute,
  NewWalletRoute,
  PortfolioV2Route,
  OrdersRoute,
  PremiumRoute,
  ProfileRoute,
  PulseV2Route,
  RootRoute,
  TradeRoute,
  WalletManagerRoute,
  WalletManagerWalletRoute,
  WalletSwapRoute,
  WalletTransferRoute,
  WatchlistRoute,
} from '@root/features/routing'
import { PremiumCurrencyRoute } from '@root/features/routing/model'
import { AuthPage } from '@root/pages/auth/auth-page'
import { NotFoundPage } from '@root/pages/common/not-found-page'
import { ExploreV2Page } from '@root/pages/explore-v2'
import { OrdersPage } from '@root/pages/orders/ui/mobile/orders-page'
import { PortfolioV2Page } from '@root/pages/portfolio-v2'
import { PremiumCurrencyPage } from '@root/pages/premium/premium-currency-page/ui/mobile'
import { NewWalletPage } from '@root/pages/wallet/new-wallet/ui/mobile/new-wallet-page'
import { WalletSwapPage } from '@root/pages/wallet/swap/ui/mobile/wallet-swap-page'
import { WatchlistPage } from '@root/pages/watchlist/ui/mobile/watchlist-page'
import { ExploreScreen } from '@root/screens/explore/explore-screen'
import { PremiumScreen } from '@root/screens/premium'
import { ProfileScreen } from '@root/screens/profile'
import { TradingScreen } from '@root/screens/trading'
import { TransferScreen } from '@root/screens/transfer'
import { WalletScreen, WalletsScreen } from '@root/screens/wallet'
import { PREMIUM_ENABLED } from '@root/shared'
import { PlatformUtil } from '@root/shared/core/platform-util'
import { DialogsProvider } from '@root/shared/ui-kit'
import { MobileLayout } from '@root/widgets/layout/mobile/ui'

import { useDeeplinkHandlers } from '../common/use-deeplink-handlers'

import { MobileDialogs } from './mobile-dialogs'

import type { ImpactHapticFeedbackStyle } from '@blade/ui-kit'

export const MobileApp = () => {
  useDeeplinkHandlers()

  const nativeCapabilitiesCtx = useMemo(
    () => ({
      hapticFeedback: {
        impactOccurred: (style: ImpactHapticFeedbackStyle) => {
          PlatformUtil.impactFeedback(style)
        },
      },
    }),
    [],
  )

  return (
    <NativeCapabilitiesContext.Provider value={nativeCapabilitiesCtx}>
      <DialogsProvider>
        <MobileGlobalStyles />

        <Routes>
          <Route path={AuthRoute.path} element={<AuthPage />} />
          {/*
            The V2 screens carry their own chrome and their own palette, and
            they are responsive down to a phone, so they are the same screens
            here as on a desktop rather than a second build of each. They sit
            outside MobileLayout for the reason they sit outside the desktop
            one: none of the old shell should appear around them.
          */}
          <Route
            path={PulseV2Route.path}
            element={
              <TerminalAccessGate>
                <ExploreV2Page />
              </TerminalAccessGate>
            }
          />
          <Route
            path={PortfolioV2Route.path}
            element={
              <TerminalAccessGate>
                <PortfolioV2Page />
              </TerminalAccessGate>
            }
          />
          <Route
            path={RootRoute.path}
            element={
              // Invite-only, as on desktop: everything below waits for access.
              <TerminalAccessGate>
                <MobileLayout />
              </TerminalAccessGate>
            }
          >
            <Route index element={<ExploreScreen />} />
            {/* Premium is hidden until it is sold; its routes go with it. */}
            {PREMIUM_ENABLED ? (
              <>
                <Route path={PremiumRoute.path} element={<PremiumScreen />} />
                <Route
                  path={PremiumCurrencyRoute.path}
                  element={<PremiumCurrencyPage />}
                />
              </>
            ) : undefined}
            <Route path={TradeRoute.path} element={<TradingScreen />} />
            <Route
              path={NewWalletRoute.path}
              element={
                <AuthenticatedOnly>
                  <NewWalletPage />
                </AuthenticatedOnly>
              }
            />
            <Route path={WalletManagerRoute.path} element={<WalletsScreen />} />
            <Route
              path={WalletManagerWalletRoute.path}
              element={
                <AuthenticatedOnly>
                  <WalletScreen />
                </AuthenticatedOnly>
              }
            />
            <Route
              path={WalletSwapRoute.path}
              element={
                <AuthenticatedOnly>
                  <WalletSwapPage />
                </AuthenticatedOnly>
              }
            />
            <Route
              path={WalletTransferRoute.path}
              element={
                <AuthenticatedOnly>
                  <TransferScreen />
                </AuthenticatedOnly>
              }
            />
            <Route
              path={WatchlistRoute.path}
              element={
                <AuthenticatedOnly>
                  <WatchlistPage />
                </AuthenticatedOnly>
              }
            />
            <Route
              path={OrdersRoute.path}
              element={
                <AuthenticatedOnly>
                  <OrdersPage />
                </AuthenticatedOnly>
              }
            />
            <Route
              path={ProfileRoute.path}
              element={
                <AuthenticatedOnly>
                  <ProfileScreen />
                </AuthenticatedOnly>
              }
            >
              {/* <Route index element={<ProfileBoard />} />
            <Route
              path={ProfileReferralLeaderboardRoute.path}
              element={<ReferralLeaderboardBoard />}
            />
            <Route
              path={ProfileTradingLeaderboardRoute.path}
              element={<TradingLeaderboardBoard />}
            />
            <Route
              path={ProfileReferralUsersRoute.path}
              element={<ReferralUsersBoard />}
            /> */}
            </Route>

            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        <MobileDialogs />
      </DialogsProvider>
    </NativeCapabilitiesContext.Provider>
  )
}

export const MobileGlobalStyles = createGlobalStyle`
  :root {
    --viewport-height: 100vh;
    --max-drawer-height: calc(var(--viewport-height) - var(--safe-top));
  }

  @supports (height: 100dvh) {
    :root {
      --viewport-height: 100dvh;
    }
  }

  body {
    height: 100vh;
  }
`
